import { Component, type ReactNode } from 'react'

import { ActionAnchor, ActionButton } from './errors/ErrorActions'
import { ErrorDetail } from './errors/ErrorDetail'
import { ErrorState } from './errors/ErrorState'
import { showErrorDetail } from './errors/devMode'

interface Props {
  children: ReactNode
}
interface State {
  error: Error | null
}

/**
 * The last resort, outside `RouterProvider` (main.tsx).
 *
 * WHAT IT ACTUALLY CATCHES, which is narrower than it looks: anything thrown
 * above or beside the router. Route failures never reach here, because React
 * Router handles its own subtree and stops at the nearest `errorElement`
 * (`RouteError`). That is not a gap to close by moving this component inside
 * the router; the two boundaries cover different halves and both are needed.
 *
 * IT WAS ON A DEAD PALETTE UNTIL NOW. The markup here was Kinetic Pulse
 * (`#0c0c1f`, cyan, rose, `backdrop-blur-xl`), a design system this project
 * retired and whose blur DESIGN.md now bans outright. It went unnoticed for as
 * long as it did precisely because nothing routed through it.
 *
 * NO `<Link>` ANYWHERE BELOW THIS POINT. There is no router above it, so the
 * way out has to be a document load. `ErrorActions` splits the two components
 * for that reason.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: unknown): void {
    console.error('[ErrorBoundary]', error, info)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <ErrorState
        eyebrow="Error"
        title="Something went wrong"
        body="The app could not finish loading. Your training data is safe."
        className="min-h-[100dvh]"
        actions={
          <>
            <ActionAnchor href="/">Back to home</ActionAnchor>
            {/* A reload, not `setState({ error: null })`. Re-rendering the same
                subtree that just threw almost always throws again, which reads
                to the user as a button that does nothing. */}
            <ActionButton onClick={() => window.location.reload()}>Try again</ActionButton>
          </>
        }
        detail={showErrorDetail() ? <ErrorDetail error={error} /> : undefined}
      />
    )
  }
}
